import React from "react";
import { useState, useEffect, useRef } from "react";
import "../css/About.css";

export default function About() {
  document.title = "About | Space Technology Students' Society";

  const SWIPE_THRESHOLD = 80;
  const MOBILE_BREAKPOINT = 768;

  const aboutContentData = [
  {
    id: "mission",
    type: "mission",
    sidebarName: "spAts",
    icon: null,
    image: "/img/logo-spats-dark-no-bg.png",
    heading: "spAts",
    tagline: "Space Technology Students' Society",
    description:
      "We are the Space Technology Student’s Society (better known as spAts), the official student body under Kalpana Chawla Space Technology Cell, which is the contact point of ISRO and IIT Kharagpur. spAts was founded when a diverse group of students sharing a common interest in space science and astronomy came together. It has since been the motto of spAts to spread awareness among the masses about the cosmos and simultaneously enhance our knowledge and understanding of space through activities and fun. Each individual of our Society has a thirst for knowledge and love being engaged in learning. Some lean towards the best technical stuff, some are interested towards the theoretical side of things, some love astronomy, reading, discussing current events and some love capturing the beauty of the night sky. We learn about stuff by building projects, having loads of discussions, exploring new skills and of course - dedicating towards space.",
    activities: []
  },
  {
    id: "fqtq",
    type: "team",
    sidebarName: "FQTQ",
    icon: "fa-brain",
    heading: "FQTQ",
    tagline: "From Quarks To Quasars",
    description:
      `FQTQ is the theoretical wing of spAts with a rather funky name - "From Quarks To Quasars", investigating the cosmos from sub-atomic particles to the largest observable structures. We engage in rigorous discussions on physics, astrophysics, and cosmology. Through regular research talks, presentations, and collaborative study, we aim to deepen our understanding of the fundamental laws governing the universe.`,
    activities: [
      "Research & Writing",
      "K-Meets",
      "Workshops and Guest Lectures",
      "Discussion Forums"
    ]
  },
  {
    id: "tech",
    type: "team",
    sidebarName: "TECH",
    icon: "fa-laptop-code",
    heading: "TECH",
    description:
      "The Tech Team serves as the digital architects of the society, engineering the platforms that connect us with the world. Responsible for developing and maintaining the official spAts websites, event portals, and internal automation tools, they ensure a seamless digital experience for thousands of participants during the National Students' Space Challenge (NSSC) and year-round activities.",
    activities: [
      "Frontend & Backend Development",
      "spAts Website Management",
      "NSSC Fest Portal Creation",
      "Innovative Solution & Automation"
    ]
  },
  {
    id: "management",
    type: "team",
    sidebarName: "MGMT",
    icon: "fa-briefcase",
    heading: "MANAGEMENT",
    description:
      "Mission Control is where strategy meets execution. The Management Team orchestrates the complex logistics behind our events, ensuring operational excellence. From handling corporate sponsorship and public relations to managing on-ground logistics for workshops and guest lectures, this team ensures that the society's ambitious initiatives are launched and executed with precision.",
    activities: [
      "Event Management",
      "Public Relations (PR)",
      "Logistics & Operations",
      "Sponsorship & Marketing"
    ]
  },
  {
    id: "design",
    type: "team",
    sidebarName: "DESIGN",
    icon: "fa-palette",
    heading: "DESIGN",
    description:
      "The Design Team is the creative force that translates complex space concepts into compelling visual narratives. They are responsible for the society's entire artistic direction—ranging from branding, UI/UX, and illustrations to video production and merchandise. By blending art with science, they ensure spAts possesses a distinct, professional, and captivating visual identity.",
    activities: [
      "Graphic Design & Illustration",
      "UI/UX Design",
      "Video Editing & VFX",
      "Merchandise Design"
    ]
  }
];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  
  // Refs for DOM elements
  const scrollableDivRefs = useRef([]);
  const mainRef = useRef(null);
  const slideRefs = useRef([]); // To track individual slides for mobile scrolling

  // State for slide logic
  const [touchStartY, setTouchStartY] = useState(null);
  const [wheel, setWheel] = useState(true);
  const [isBottom, setIsBottom] = useState(false);
  const [isTop, setIsTop] = useState(true);

  // Detect mobile/desktop viewport changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile scroll spy: Highlights sidebar item for section closest to viewport center
  useEffect(() => {
    if (!isMobile) return;

    const container = document.querySelector('.mobile-view');
    if (!container) return;

    const handleScroll = () => {
      const viewportCenter = container.scrollTop + container.clientHeight / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      // Find section closest to viewport center
      slideRefs.current.forEach((ref, index) => {
        if (ref) {
          const containerTop = container.getBoundingClientRect().top;
          const elementTop = ref.getBoundingClientRect().top - containerTop + container.scrollTop;
          const elementCenter = elementTop + ref.offsetHeight / 2;
          const distance = Math.abs(viewportCenter - elementCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        }
      });

      setCurrentIndex(closestIndex);
    };

    handleScroll(); // Initial check

    // Throttled scroll listener
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', scrollListener, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      container.removeEventListener('scroll', scrollListener);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isMobile]);

  // Desktop: Track scroll position within each content section
  const checkScroll = (index) => {
    if (isMobile) return;
    const el = scrollableDivRefs.current[index];
    if (!el) return;
    const isAtTop = el.scrollTop <= 1;
    const isAtBottom = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) <= 2;
    setIsTop(isAtTop);
    setIsBottom(isAtBottom);
  };

  useEffect(() => {
    if (isMobile) return;
    
    const listeners = [];
    
    // Desktop: Monitor scroll position in each content section
    scrollableDivRefs.current.forEach((ref, index) => {
      if (ref) {
        const handler = () => checkScroll(index);
        ref.addEventListener("scroll", handler);
        listeners.push({ ref, handler });
        checkScroll(index);
      }
    });

    // Reset Top/Bottom state on slide change
    setIsTop(true);
    if (scrollableDivRefs.current[currentIndex]) {
      const el = scrollableDivRefs.current[currentIndex];
      if (el.scrollHeight <= el.clientHeight) setIsBottom(true);
    }

    // Cleanup
    return () => {
      listeners.forEach(({ ref, handler }) => {
        ref.removeEventListener("scroll", handler);
      });
    };
  }, [currentIndex, isMobile]);

  // Desktop navigation: Next/Previous section handlers
  const handleNext = React.useCallback(() => {
    if (isMobile) return; // Disable on mobile
    if (!isBottom && scrollableDivRefs.current[currentIndex]?.scrollHeight > scrollableDivRefs.current[currentIndex]?.clientHeight) return;
    setCurrentIndex((prev) => (prev < aboutContentData.length - 1 ? prev + 1 : prev));
  }, [isMobile, isBottom, currentIndex, aboutContentData.length]);

  const handlePrev = React.useCallback(() => {
    if (isMobile) return; // Disable on mobile
    if (!isTop && scrollableDivRefs.current[currentIndex]?.scrollHeight > scrollableDivRefs.current[currentIndex]?.clientHeight) return;
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, [isMobile, isTop, currentIndex]);

  // Desktop navigation: Touch gestures
  const handleTouchStart = (e) => {
    if (isMobile) return;
    setTouchStartY(e.touches[0].clientY);
  }

  const handleTouchMove = (e) => {
    if (isMobile || !touchStartY) return;
    const deltaY = e.touches[0].clientY - touchStartY;
    if (deltaY > SWIPE_THRESHOLD) {
      handlePrev();
      setTouchStartY(null);
    } else if (deltaY < -SWIPE_THRESHOLD) {
      handleNext();
      setTouchStartY(null);
    }
  };

  const handleWheel = React.useCallback((e) => {
    if (isMobile || !wheel) return;
    
    setWheel(false);
    setTimeout(() => setWheel(true), 800);
    
    if (e.deltaY > 0) handleNext();
    else if (e.deltaY < 0) handlePrev();
  }, [isMobile, wheel, handleNext, handlePrev]);

  // Desktop navigation: Arrow key controls
  useEffect(() => {
    if (isMobile) return;
    
    const handleKeyDown = (e) => {
      if (e.keyCode === 40) handleNext();
      if (e.keyCode === 38) handlePrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, handleNext, handlePrev]);

  // Desktop navigation: Wheel events (non-passive to prevent default)
  useEffect(() => {
    if (isMobile) return;
    
    const mainElement = mainRef.current;
    if (!mainElement) return;

    mainElement.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      mainElement.removeEventListener("wheel", handleWheel);
    };
  }, [wheel, currentIndex, isBottom, isTop, isMobile]);


  // Sidebar click handler
  const handleNavClick = (index) => {
    if (isMobile) {
      // Mobile: Smooth scroll to center section
      slideRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setCurrentIndex(index);
    }
  };

  return (
    <div className={`about ${isMobile ? 'mobile-view about-scroll-container' : ''}`}>
      {/* --- SIDEBAR --- */}
      <div className="about-sidebar">
        <div className="sidebar-nav-list">
          <div className="sidebar-chalk-line"></div>
          {aboutContentData.map((element, index) => (
            <React.Fragment key={index}>
              {index === 1 && (
                <div className="sidebar-header-wrapper">
                  <div className="sidebar-header-dot"></div>
                  <span className="sidebar-section-header">TEAMS</span>
                </div>
              )}
              <div
                className={`about-sidebar-element ${currentIndex === index ? "active" : ""}`}
                onClick={() => handleNavClick(index)}
              >
                <div className="sidebar-ball-container">
                  <div className="sidebar-ball"></div>
                </div>
                <div className="sidebar-label">
                  <span className="sidebar-text">{element.sidebarName}</span>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* --- MAIN CONTENT --- */}
      <div
        className="about-main"
        ref={mainRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setTouchStartY(null)}
      >
        <div
          className="about-slider-track"
          style={!isMobile ? { transform: `translateY(-${currentIndex * (window.innerHeight - 80)}px)` } : {}}
        >
          {aboutContentData.map((data, index) => {
            const isMission = data.type === "mission";

            return (
              <div 
                className="about-slide" 
                key={index}
                ref={(el) => (slideRefs.current[index] = el)}
                data-index={index}
              >
                <div
                  className="about-scroll-container"
                  ref={(el) => (scrollableDivRefs.current[index] = el)}
                >
                  {/* Layout Wrapper: 
                      Desktop: alternates direction for each section.
                      Mobile: CSS forces column, ignoring the style.
                  */}
                  <div
                    className={`team-layout ${index % 2 === 0 ? 'reverse-layout' : ''}`}
                  >

                    {/* VISUAL SIDE */}
                    <div className="team-left">
                      <div className="sketchy-circle">
                        {isMission ? (
                          <img src={data.image} alt="spAts Logo" className="visual-logo" />
                        ) : (
                          <i className={`fas ${data.icon} team-icon`}></i>
                        )}
                      </div>
                    </div>

                    {/* CONTENT SIDE */}
                    <div className="team-right">
                      <div className="chalk-box">
                        <h2 className="team-title" data-icon={data.icon || ''}>
                          {isMission && data.image && (
                            <img src={data.image} alt="spAts" className="team-title-icon team-title-logo" />
                          )}
                          {!isMission && data.icon && (
                            <i className={`fas ${data.icon} team-title-icon`}></i>
                          )}
                          <span>{data.heading}</span>
                        </h2>
                        {data.tagline && <h4 className="team-tagline">{data.tagline}</h4>}
                        <div className="chalk-divider"></div>

                        <p className="team-desc">{data.description}</p>

                        {data.activities.length > 0 && (
                          <div className="team-section">
                            <h5>Activities</h5>
                            <ul className="team-list">
                              {data.activities.map((act, i) => (
                                <li key={i}>{act}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Social Icons */}
      <div className="about-social-media-icons">
        {[
          { link: "https://www.facebook.com/spAtsiitkgp/", icon: "fa-facebook" },
          { link: "https://www.instagram.com/spats.nssc.iitkgp/", icon: "fa-instagram" },
          { link: "https://www.linkedin.com/company/spats/", icon: "fa-linkedin" },
          { link: "https://www.youtube.com/@spAtsiitkgp", icon: "fa-youtube-play" },
          { link: "https://x.com/spats_iitkgp", icon: "fa-twitter" },
        ].map((social, i) => (
          <div className="about-social-media-icons-link" key={i}>
            <a href={social.link} target="_blank" rel="noreferrer">
              <i className={`fab ${social.icon} fa ${social.icon}`}></i>  
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}